import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Divider,
  Table, TableBody, TableCell, TableRow,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  InputAdornment, Collapse, Alert, Chip, CircularProgress,
  Stack, LinearProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PaymentsIcon from '@mui/icons-material/Payments';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AssignmentIcon from '@mui/icons-material/Assignment';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import { PATIENT_FORM_KEYS, FORM_SECTIONS } from '../../constants/ascForms';
import { AnestheticQForm, MhQForm, RespPartyForm } from '../asc/AscFormPanels';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import AmendmentRequestButton from './AmendmentRequestButton';

const API_BASE = process.env.REACT_APP_API_BASE
  || process.env.REACT_APP_API_URL
  || (typeof window !== 'undefined' && window.location.port === '3000' ? 'http://localhost:3001' : '');

const fmtCurrency = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

const fmtDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

// Comma-formatted currency input — matches the convention used elsewhere in the
// app (e.g. PracticeCostBasisPage's CurrencyInput): shows "23,744.53" at rest,
// drops to the raw editable number while focused.
const formatDollarInput = (value) => {
  const n = Number(String(value).replace(/,/g, ''));
  return n ? n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : '';
};
function CurrencyInput({ value, onChange, ...props }) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState(formatDollarInput(value));
  useEffect(() => { if (!focused) setDraft(formatDollarInput(value)); }, [focused, value]);
  const handleChange = (event) => {
    const next = event.target.value.replace(/,/g, '');
    if (!/^\d*(\.\d{0,2})?$/.test(next)) return;
    setDraft(next);
    onChange(next);
  };
  return (
    <TextField
      {...props}
      type="text"
      value={draft}
      onFocus={() => { setFocused(true); setDraft(Number(String(value).replace(/,/g, '')) ? String(value).replace(/,/g, '') : ''); }}
      onBlur={() => { setFocused(false); setDraft(formatDollarInput(draft)); }}
      onChange={handleChange}
      inputProps={{ inputMode: 'decimal', ...(props.inputProps || {}) }}
      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, ...(props.InputProps || {}) }}
    />
  );
}

const stripePromiseCache = {};
function getStripePromise(key, connectedAccountId) {
  if (!key) return null;
  const cacheKey = `${key}:${connectedAccountId || 'direct'}`;
  if (!stripePromiseCache[cacheKey]) stripePromiseCache[cacheKey] = loadStripe(key, connectedAccountId ? { stripeAccount: connectedAccountId } : undefined);
  return stripePromiseCache[cacheKey];
}

// ─── Tab panel helper ─────────────────────────────────────────────────────────
function TabPanel({ value, index, children }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// ─── Main SurgicalPortalPage ──────────────────────────────────────────────────
export default function SurgicalPortalPage({
  token, invoice, estimates, simplified, stripePublishableKey, stripeConnectedAccountId, onPaymentSuccess,
  amendmentRequestedAt, onAmendmentRequestSubmitted,
}) {
  const [activeTab, setActiveTab] = useState(invoice?.surgeryAppointment ? 1 : 0);

  const abandonedEstimates = (estimates || []).filter(e => e.status === 'abandoned' || e.status === 'superseded');

  const tabs = [
    { label: 'Payments',        icon: <PaymentsIcon fontSize="small" /> },
    { label: 'Surgery Details', icon: <EventNoteIcon fontSize="small" /> },
    { label: 'Pre-Op Forms',    icon: <AssignmentIcon fontSize="small" /> },
    { label: 'Instructions',    icon: <MenuBookIcon fontSize="small" /> },
  ];

  return (
    <Box>
      <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 2 }}
        >
          {tabs.map((tab, i) => (
            <Tab
              key={i}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
              sx={{ minHeight: 56, textTransform: 'none', fontWeight: 600 }}
            />
          ))}
        </Tabs>
      </Paper>

      {/* ── Payments ── */}
      <TabPanel value={activeTab} index={0}>
        <PaymentsTab
          token={token}
          invoice={invoice}
          simplified={simplified}
          abandonedEstimates={abandonedEstimates}
          stripePublishableKey={stripePublishableKey}
          stripeConnectedAccountId={stripeConnectedAccountId}
          onPaymentSuccess={onPaymentSuccess}
          amendmentRequestedAt={amendmentRequestedAt}
          onAmendmentRequestSubmitted={onAmendmentRequestSubmitted}
        />
      </TabPanel>

      {/* ── Surgery Details ── */}
      <TabPanel value={activeTab} index={1}>
        <SurgeryDetailsTab invoice={invoice} />
      </TabPanel>

      {/* ── Pre-Op Forms ── */}
      <TabPanel value={activeTab} index={2}>
        <PreOpFormsTab token={token} invoice={invoice} />
      </TabPanel>

      {/* ── Instructions ── */}
      <TabPanel value={activeTab} index={3}>
        <InstructionsTab />
      </TabPanel>

    </Box>
  );
}

// ─── Payments Tab ─────────────────────────────────────────────────────────────
function PaymentsTab({ token, invoice, simplified, abandonedEstimates, stripePublishableKey, stripeConnectedAccountId, onPaymentSuccess, amendmentRequestedAt, onAmendmentRequestSubmitted }) {
  const [showAbandoned, setShowAbandoned] = useState(false);
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payClientSecret, setPayClientSecret] = useState(null);
  const [payIntentError, setPayIntentError] = useState(null);
  const [payIntentLoading, setPayIntentLoading] = useState(false);

  const isPaidInFull = invoice.invoiceStatus === 'paid_in_full';
  const stripePromise = stripePublishableKey ? getStripePromise(stripePublishableKey, stripeConnectedAccountId) : null;

  const handleOpenPayDialog = () => {
    setPayAmount(invoice.remaining > 0 ? String(invoice.remaining.toFixed(2)) : '');
    setPayClientSecret(null);
    setPayIntentError(null);
    setPayDialogOpen(true);
  };

  const handleCreateIntent = async () => {
    const amt = parseFloat(payAmount);
    if (!amt || amt <= 0) return;
    setPayIntentLoading(true);
    setPayIntentError(null);
    try {
      const res = await fetch(`${API_BASE}/api/estimates/share/${token}/payment/intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: amt }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create payment intent');
      setPayClientSecret(data.clientSecret);
    } catch (err) {
      setPayIntentError(err.message);
    } finally {
      setPayIntentLoading(false);
    }
  };

  const paymentRows = invoice.payments || [];
  const gatewayLabel = (p) => {
    if (p.gateway === 'stripe') return p.cardBrand ? `${p.cardBrand.toUpperCase()} ···${p.cardLast4}` : 'Credit Card';
    if (p.gateway === 'cash') return 'Cash';
    if (p.gateway === 'check') return 'Check';
    if (p.gateway === 'cherry') return 'Cherry Financing';
    if (p.gateway === 'carecredit') return 'CareCredit';
    return p.method || p.gateway || '—';
  };

  const acceptedEstimate = invoice.estimate ? {
    ...invoice.estimate,
    variation_label: invoice.estimate.label,
    total_amount: invoice.estimate.totalAmount,
  } : null;

  return (
    <Box>
      {/* Accepted Estimate */}
      {acceptedEstimate && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              {acceptedEstimate.variation_label || 'Accepted Estimate'}
            </Typography>
            <Box className="no-print">
              <AmendmentRequestButton
                token={token}
                fixedEstimate={{ estimate_id: acceptedEstimate.estimateId, variation_label: acceptedEstimate.variation_label }}
                existingRequestAt={amendmentRequestedAt}
                onSubmitted={onAmendmentRequestSubmitted}
                variant="outlined"
                color="warning"
                size="small"
                label="Request an Amendment"
              />
            </Box>
          </Box>
          <EstimateCard estimate={acceptedEstimate} simplified={simplified} />
        </Box>
      )}

      {/* Financial Policy download */}
      <Paper sx={{ p: 3, mb: 3 }} elevation={1}>
        <Typography variant="h6" fontWeight={700} gutterBottom>Financial Policy</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Your signed Self-Pay Financial Policy is on file.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          className="no-print"
          onClick={async () => {
            try {
              const res = await fetch(`${API_BASE}/api/estimates/share/${token}/policy/pdf`);
              if (!res.ok) throw new Error('PDF not available');
              const blob = await res.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'financial-policy.pdf';
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);
            } catch {
              alert('Unable to download policy PDF. Please contact the office.');
            }
          }}
        >
          Download Signed Financial Policy
        </Button>
      </Paper>

      {/* Payment Summary */}
      <Paper sx={{ p: 3, mb: 3 }} elevation={1}>
        <Typography variant="h6" fontWeight={700} gutterBottom>Payment Summary</Typography>

        {/* Estimate total */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">Estimate Total</Typography>
          <Typography variant="body2" fontWeight={600}>{fmtCurrency(invoice.estimateTotal)}</Typography>
        </Box>

        {/* Individual payment rows */}
        {paymentRows.map((p) => {
          const isDeposit = p.reason === 'Surgical Estimate Deposit';
          return (
            <Box key={p.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box>
                <Typography variant="body2" color="success.main" fontWeight={500}>
                  − {isDeposit ? 'Deposit' : 'Payment'}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">{fmtDate(p.date)}</Typography>
                <Typography variant="caption" color="text.secondary" display="block">{gatewayLabel(p)}</Typography>
                {p.enteredBy && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {p.enteredBy === 'patient' ? `Patient: ${p.patientName || 'Patient'}` : `Staff: ${p.enteredBy}`}
                  </Typography>
                )}
              </Box>
              <Typography variant="body2" color="success.main" fontWeight={600} sx={{ ml: 2, whiteSpace: 'nowrap' }}>
                −{fmtCurrency(p.amount)}
              </Typography>
            </Box>
          );
        })}

        {/* Remaining balance */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1.5, mt: 0.5, borderTop: '2px solid', borderColor: 'primary.main' }}>
          <Typography variant="body1" fontWeight={700}>
            {isPaidInFull ? 'Paid in Full' : 'Remaining Balance'}
          </Typography>
          <Typography variant="h6" fontWeight={800} color={isPaidInFull ? 'success.main' : 'primary.main'}>
            {fmtCurrency(invoice.remaining)}
          </Typography>
        </Box>

        {!isPaidInFull && (
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={handleOpenPayDialog} className="no-print">
              Make a Payment
            </Button>
          </Box>
        )}
      </Paper>

      {/* Abandoned Estimate Options */}
      {abandonedEstimates?.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Button
            variant="text"
            size="small"
            onClick={() => setShowAbandoned(v => !v)}
            endIcon={showAbandoned ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            className="no-print"
          >
            {showAbandoned ? 'Hide' : 'View'} Abandoned / Superseded Estimate Options ({abandonedEstimates.length})
          </Button>
          <Collapse in={showAbandoned}>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              These options were not selected or have been superseded.
            </Typography>
            {abandonedEstimates.map(est => (
              <Box key={est.estimate_id} sx={{ mb: 2 }}>
                <EstimateCard estimate={est} simplified={simplified} />
              </Box>
            ))}
          </Collapse>
        </Box>
      )}

      {/* Make a Payment dialog */}
      <Dialog open={payDialogOpen} onClose={() => setPayDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Make a Payment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Remaining balance: <strong>{fmtCurrency(invoice.remaining)}</strong>
          </Typography>
          {!payClientSecret ? (
            <>
              <CurrencyInput
                label="Payment Amount"
                value={payAmount}
                onChange={setPayAmount}
                fullWidth
                sx={{ mb: 2 }}
              />
              <Alert severity="info" sx={{ mb: 2 }}>
                This pays by credit card online. Interested in financing, or paying by cash or check instead? Please call our office.
              </Alert>
              {payIntentError && <Alert severity="error" sx={{ mb: 2 }}>{payIntentError}</Alert>}
            </>
          ) : (
            stripePromise && (
              <Elements stripe={stripePromise} options={{ clientSecret: payClientSecret }}>
                <StripePaymentForm
                  endpoint={`${API_BASE}/api/estimates/share/${token}/payment/confirm`}
                  onSuccess={() => { setPayDialogOpen(false); onPaymentSuccess?.(); }}
                  submitLabel="Submit Payment"
                />
              </Elements>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayDialogOpen(false)}>Cancel</Button>
          {!payClientSecret && (
            <Button
              variant="contained"
              onClick={handleCreateIntent}
              disabled={payIntentLoading || !payAmount || parseFloat(payAmount) <= 0}
            >
              {payIntentLoading ? 'Processing…' : 'Continue'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Surgery Details Tab ──────────────────────────────────────────────────────
function SurgeryDetailsTab({ invoice }) {
  const appt = invoice?.surgeryAppointment;

  const fmtTime = (t) => {
    if (!t) return null;
    const [h, m] = t.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  const fmtDuration = (seconds) => {
    if (!seconds) return null;
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hrs && mins) return `${hrs} hr ${mins} min`;
    if (hrs) return `${hrs} hr`;
    return `${mins} min`;
  };

  const fmtDateSafe = (d) => {
    if (!d) return null;
    const [y, m, day] = d.split('-').map(Number);
    return new Date(y, m - 1, day).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  const rows = appt ? [
    appt.facilityName  && { label: 'Facility',                value: appt.facilityName },
    appt.eventDate     && { label: 'Surgery Date',            value: fmtDateSafe(appt.eventDate) },
    appt.startTime     && { label: 'Surgery Time',            value: fmtTime(appt.startTime) },
    appt.duration      && { label: 'Estimated Duration',      value: fmtDuration(appt.duration) },
    appt.rta           && { label: 'Requested Arrival Time',  value: fmtTime(appt.rta) },
  ].filter(Boolean) : [];

  return (
    <Paper sx={{ p: 3 }} elevation={1}>
      <Typography variant="h6" fontWeight={700} gutterBottom>Surgery Details</Typography>

      {invoice?.surgeonName && (
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Surgeon:</strong> {invoice.surgeonName}
        </Typography>
      )}

      {rows.length > 0 ? (
        <Table size="small" sx={{ mt: 1 }}>
          <TableBody>
            {rows.map(({ label, value }) => (
              <TableRow key={label} sx={{ '&:last-child td': { border: 0 } }}>
                <TableCell sx={{ pl: 0, fontWeight: 600, color: 'text.secondary', width: '45%', fontSize: '0.85rem' }}>
                  {label}
                </TableCell>
                <TableCell sx={{ fontSize: '0.95rem' }}>{value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Surgery scheduling details, facility information, and pre-operative instructions will appear here once confirmed by the office.
        </Typography>
      )}
    </Paper>
  );
}

// ─── Pre-Op Forms Tab ─────────────────────────────────────────────────────────
function PreOpFormsTab({ token, invoice }) {
  const patientForms = FORM_SECTIONS
    .flatMap(s => s.forms)
    .filter(f => PATIENT_FORM_KEYS.includes(f.key));

  const [activeFormKey, setActiveFormKey] = useState(patientForms[0]?.key || null);
  const hasAutoSelectedRef = useRef(false);
  const [formData, setFormData] = useState({});
  const [completedForms, setCompletedForms] = useState({});
  const [editingForms, setEditingForms] = useState({}); // { [formKey]: true } when unlocked for editing
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedOk, setSavedOk] = useState(false);
  const saveTimer = useRef(null);
  const formDataRef = useRef({});
  useEffect(() => { formDataRef.current = formData; }, [formData]);

  const hasSurgeryDate = !!(invoice?.hasSurgeryRequest || invoice?.surgeryAppointment?.eventDate);

  const fetchFormData = useCallback(async (formKey, { showLoader } = {}) => {
    if (!token || !formKey) return;
    if (showLoader) setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/estimates/share/${token}/forms/${formKey}`);
      const data = await res.json();
      if (data.success && data.form) {
        setFormData(prev => ({ ...prev, [formKey]: data.form.formData || {} }));
        if (data.form.completedAt) {
          setCompletedForms(prev => ({ ...prev, [formKey]: data.form.completedAt }));
        }
      } else {
        setFormData(prev => ({ ...prev, [formKey]: {} }));
      }
    } catch (e) {
      setFormData(prev => ({ ...prev, [formKey]: {} }));
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [token]);

  // On mount: load all forms in parallel so sidebar icons and active form locked state are correct immediately
  useEffect(() => {
    if (!token) return;
    const keys = FORM_SECTIONS.flatMap(s => s.forms).filter(f => PATIENT_FORM_KEYS.includes(f.key)).map(f => f.key);
    keys.forEach((key, idx) => {
      fetchFormData(key, { showLoader: idx === 0 });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Once completion data loads, auto-select the first incomplete form (runs only once)
  useEffect(() => {
    if (hasAutoSelectedRef.current) return;
    const loadedKeys = Object.keys(formData);
    if (loadedKeys.length < patientForms.length) return; // wait until all forms have loaded
    hasAutoSelectedRef.current = true;
    const firstIncomplete = patientForms.find(f => !completedForms[f.key]);
    if (firstIncomplete) setActiveFormKey(firstIncomplete.key);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, completedForms]);

  // Re-load when switching to a different form tab (but not on initial mount — covered above)
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) { mountedRef.current = true; return; }
    if (activeFormKey) fetchFormData(activeFormKey, { showLoader: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFormKey]);

  const saveForm = useCallback(async (formKey, data, markComplete) => {
    if (!token || !formKey) return;
    setSaving(true);
    setSaveError(null);
    try {
      const body = {
        formData: data,
        completedAt: markComplete ? new Date().toISOString() : null,
      };
      const res = await fetch(`${API_BASE}/api/estimates/share/${token}/forms/${formKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const resp = await res.json();
      if (!res.ok) throw new Error(resp.error || 'Save failed');
      if (markComplete) {
        setCompletedForms(prev => ({ ...prev, [formKey]: new Date().toISOString() }));
        setEditingForms(prev => ({ ...prev, [formKey]: false }));
        setSavedOk(true);
        setTimeout(() => setSavedOk(false), 3000);
      }
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  }, [token]);

  const updateAnswer = (formKey, questionKey, value) => {
    setFormData(prev => ({
      ...prev,
      [formKey]: { ...(prev[formKey] || {}), [questionKey]: value }
    }));
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveForm(formKey, { ...(formDataRef.current[formKey] || {}), [questionKey]: value }, false);
    }, 1500);
  };

  const updateSubField = (formKey, questionKey, subKey, value) => {
    setFormData(prev => {
      const existing = prev[formKey] || {};
      const q = existing[questionKey] || {};
      return { ...prev, [formKey]: { ...existing, [questionKey]: { ...q, [subKey]: value } } };
    });
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const current = formDataRef.current[formKey] || {};
      const updated = { ...current, [questionKey]: { ...(current[questionKey] || {}), [subKey]: value } };
      saveForm(formKey, updated, false);
    }, 1500);
  };

  const activeForm = patientForms.find(f => f.key === activeFormKey);
  const currentData = formData[activeFormKey] || {};
  const isComplete = !!completedForms[activeFormKey];

  const completedCount = patientForms.filter(f => completedForms[f.key]).length;
  const progress = patientForms.length > 0 ? (completedCount / patientForms.length) * 100 : 0;

  if (!hasSurgeryDate) {
    return (
      <Paper sx={{ p: 3 }} elevation={1}>
        <Typography variant="h6" fontWeight={700} gutterBottom>Pre-Operative Forms</Typography>
        <Alert severity="info" sx={{ mt: 1 }}>
          Your forms will become available once your surgery date has been confirmed. Please check back after your appointment is scheduled.
        </Alert>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Progress header */}
      <Paper sx={{ p: 2.5, mb: 2 }} elevation={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>Pre-Operative Forms</Typography>
          <Typography variant="body2" color="text.secondary">
            {completedCount} of {patientForms.length} completed
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ height: 6, borderRadius: 3, bgcolor: 'grey.200', '& .MuiLinearProgress-bar': { bgcolor: progress === 100 ? 'success.main' : 'primary.main' } }}
        />
        {progress === 100 && (
          <Alert severity="success" sx={{ mt: 1.5, py: 0.5 }}>All forms completed — thank you!</Alert>
        )}
      </Paper>

      {/* Form selector + content */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        {/* Sidebar nav */}
        <Paper elevation={1} sx={{ minWidth: 200, flexShrink: 0 }}>
          {patientForms.map(f => {
            const done = !!completedForms[f.key];
            const active = f.key === activeFormKey;
            return (
              <Box
                key={f.key}
                onClick={() => setActiveFormKey(f.key)}
                sx={{
                  px: 2, py: 1.5, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1.5,
                  bgcolor: active ? (done ? '#f0fdf4' : 'primary.50') : (done ? '#f9fef9' : 'transparent'),
                  borderLeft: active ? '3px solid' : '3px solid transparent',
                  borderColor: active ? (done ? 'success.main' : 'primary.main') : 'transparent',
                  '&:hover': { bgcolor: done ? '#f0fdf4' : (active ? 'primary.50' : 'grey.50') },
                  borderBottom: '1px solid', borderBottomColor: 'divider',
                }}
              >
                {(() => {
                  const hasData = Object.keys(formData[f.key] || {}).length > 0;
                  if (done) return <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main', flexShrink: 0 }} />;
                  if (hasData) return <RadioButtonCheckedIcon sx={{ fontSize: 20, color: 'success.light', flexShrink: 0 }} />;
                  return <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: active ? 'primary.main' : 'text.disabled', flexShrink: 0 }} />;
                })()}
                <Box>
                  <Typography variant="body2" fontWeight={active ? 700 : 500} color={done ? 'success.dark' : 'text.primary'} sx={{ lineHeight: 1.3 }}>
                    {f.label}
                  </Typography>
                  {(() => {
                    const hasData = Object.keys(formData[f.key] || {}).length > 0;
                    const caption = done ? 'Completed' : hasData ? 'In progress' : f.completedBy;
                    const color = done ? 'success.main' : hasData ? 'warning.main' : 'text.secondary';
                    return <Typography variant="caption" color={color}>{caption}</Typography>;
                  })()}
                </Box>
              </Box>
            );
          })}
        </Paper>

        {/* Form panel */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {loading ? (
            <Paper elevation={1} sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Paper>
          ) : activeFormKey === 'anesthetic_q' ? (
            <AnestheticQForm
              data={currentData}
              isComplete={isComplete}
              isEditing={!!editingForms['anesthetic_q']}
              onStartEdit={() => setEditingForms(prev => ({ ...prev, anesthetic_q: true }))}
              saving={saving}
              savedOk={savedOk}
              saveError={saveError}
              onUpdateAnswer={(key, val) => updateAnswer('anesthetic_q', key, val)}
              onUpdateSubField={(qKey, subKey, val) => updateSubField('anesthetic_q', qKey, subKey, val)}
              onSubmit={() => { if (saveTimer.current) clearTimeout(saveTimer.current); saveForm('anesthetic_q', currentData, true); }}
            />
          ) : activeFormKey === 'resp_party' ? (
            <RespPartyForm
              data={currentData}
              isComplete={isComplete}
              isEditing={!!editingForms['resp_party']}
              onStartEdit={() => setEditingForms(prev => ({ ...prev, resp_party: true }))}
              saving={saving}
              savedOk={savedOk}
              saveError={saveError}
              onUpdate={(key, val) => updateAnswer('resp_party', key, val)}
              onSubmit={() => { if (saveTimer.current) clearTimeout(saveTimer.current); saveForm('resp_party', currentData, true); }}
            />
          ) : activeFormKey === 'mh_q' ? (
            <MhQForm
              data={currentData}
              isComplete={isComplete}
              isEditing={!!editingForms['mh_q']}
              onStartEdit={() => setEditingForms(prev => ({ ...prev, mh_q: true }))}
              saving={saving}
              savedOk={savedOk}
              saveError={saveError}
              onUpdate={(key, val) => updateAnswer('mh_q', key, val)}
              onSubmit={() => { if (saveTimer.current) clearTimeout(saveTimer.current); saveForm('mh_q', currentData, true); }}
            />
          ) : (
            <Paper elevation={1} sx={{ p: 3 }}>
              <Typography color="text.secondary">Select a form to begin.</Typography>
            </Paper>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ─── Instructions Tab ─────────────────────────────────────────────────────────
function InstructionsTab() {
  const [preOpOpen, setPreOpOpen] = useState(true);
  const [postOpOpen, setPostOpOpen] = useState(false);

  return (
    <Box>
      {/* Pre-Op Instructions */}
      <Paper sx={{ mb: 2 }} elevation={1}>
        <Box
          sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => setPreOpOpen(v => !v)}
        >
          <Typography variant="h6" fontWeight={700}>Pre-Operative Instructions</Typography>
          {preOpOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
        <Collapse in={preOpOpen}>
          <Divider />
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Pre-operative instructions will be provided by the office prior to your surgery date.
            </Typography>
          </Box>
        </Collapse>
      </Paper>

      {/* Post-Op Instructions */}
      <Paper elevation={1}>
        <Box
          sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => setPostOpOpen(v => !v)}
        >
          <Typography variant="h6" fontWeight={700}>Post-Operative Instructions</Typography>
          {postOpOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
        <Collapse in={postOpOpen}>
          <Divider />
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Post-operative instructions will be provided by the office following your procedure.
            </Typography>
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
}

// ─── EstimateCard ─────────────────────────────────────────────────────────────
function EstimateCard({ estimate, simplified }) {
  const stages = estimate.stages || [];
  const grandTotal = parseFloat(estimate.total_amount) || 0;
  const hasMultipleStages = stages.length > 1;

  const allItems = stages.flatMap(s => s.items || []);
  const originalTotal = allItems.reduce((sum, i) => sum + (i.quantity || 1) * (i.unitPrice || 0), 0);
  const totalSavings = originalTotal - grandTotal;
  const hasSavings = totalSavings > 0.005;

  return (
    <Paper elevation={1} sx={{ overflow: 'hidden' }}>
      <Box sx={{ px: 3, py: 2, bgcolor: 'primary.50', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" fontWeight={700}>{estimate.variation_label}</Typography>
        {estimate.notes && <Typography variant="body2" color="text.secondary">{estimate.notes}</Typography>}
      </Box>

      {stages.map((stage, sIdx) => {
        const items = stage.items || [];
        const stageTotal = items.reduce((sum, i) => sum + (i.net ?? i.unitPrice ?? 0), 0);
        const procedures = items.filter(i => i.itemType === 'procedure');
        const fees = items.filter(i => i.itemType === 'fee');
        const supplies = items.filter(i => i.itemType === 'supply');
        const others = items.filter(i => i.itemType === 'custom');

        const simplifiedNonProfItems = simplified ? (() => {
          const anesItems = fees.filter(i => i._feeType === 'anesthesia');
          const combinedNet = [
            ...fees.filter(i => i._feeType !== 'anesthesia'),
            ...supplies,
          ].reduce((s, i) => s + (i.net ?? (i.quantity || 1) * (i.unitPrice || 0)), 0);
          const facilityRows = combinedNet > 0
            ? [{ itemType: 'fee', itemName: 'Facility Fee', quantity: 1, unitPrice: combinedNet, net: combinedNet }]
            : [];
          return [...anesItems, ...facilityRows];
        })() : null;

        return (
          <Box key={sIdx}>
            {hasMultipleStages && (
              <Box sx={{ px: 3, py: 1, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                  {stage.stageLabel}
                </Typography>
              </Box>
            )}
            <Box sx={{ px: 3, py: 2 }}>
              {procedures.length > 0 && (
                <ItemSection title="Professional Fees" items={procedures} simplified={simplified} />
              )}
              {simplified ? (
                simplifiedNonProfItems.length > 0 && (
                  <ItemSection title="Non-Professional Fees" items={simplifiedNonProfItems} simplified={true} />
                )
              ) : (
                fees.length > 0 && (
                  <ItemSection title="Non-Professional Fees" items={fees} simplified={false} />
                )
              )}
              {!simplified && supplies.length > 0 && (
                <ItemSection title="Supplies" items={supplies} simplified={false} />
              )}
              {others.length > 0 && (
                <ItemSection title="Other" items={others} simplified={simplified} />
              )}
              {hasMultipleStages && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {stage.stageLabel} Total: {fmtCurrency(stageTotal)}
                  </Typography>
                </Box>
              )}
            </Box>
            {sIdx < stages.length - 1 && <Divider />}
          </Box>
        );
      })}

      <Box sx={{ px: 3, py: 2, bgcolor: 'grey.50', borderTop: '2px solid', borderColor: 'primary.main' }}>
        {hasSavings && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 3, mb: 0.75 }}>
            <Typography variant="caption" color="text.secondary">Original Total</Typography>
            <Typography variant="body2" color="text.disabled" sx={{ textDecoration: 'line-through', minWidth: 90, textAlign: 'right' }}>
              {fmtCurrency(originalTotal)}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 3 }}>
          <Typography variant="subtitle1" fontWeight={700}>Estimated Total</Typography>
          <Typography variant="h6" fontWeight={800} color="primary.main" sx={{ minWidth: 90, textAlign: 'right' }}>{fmtCurrency(grandTotal)}</Typography>
        </Box>
        {hasSavings && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 3, mt: 0.5 }}>
            <Typography variant="caption" color="success.main" fontWeight={600}>You save</Typography>
            <Typography variant="body2" fontWeight={700} color="success.main" sx={{ minWidth: 90, textAlign: 'right' }}>
              {fmtCurrency(totalSavings)}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

// ─── ItemSection ──────────────────────────────────────────────────────────────
function ItemSection({ title, items, simplified }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
        {title}
      </Typography>
      <Table size="small">
        <TableBody>
          {items.map((item, idx) => {
            const hasDiscount = item.discountType && item.discountType !== 'none' && item.discountValue > 0;
            const gross = (item.quantity || 1) * (item.unitPrice || 0);
            const showDescription = item.itemDescription && !(simplified && item.itemType === 'fee');
            return (
              <TableRow key={idx} sx={{ '& td': { border: 'none', py: 0.5 } }}>
                <TableCell sx={{ pl: 0, width: '55%' }}>
                  <Typography variant="body2" fontWeight={500}>{item.itemName}</Typography>
                  {showDescription && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {item.itemDescription}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center" sx={{ width: '15%' }}>
                  {item.quantity > 1 && (
                    <Typography variant="caption" color="text.secondary">× {item.quantity}</Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ width: '15%' }}>
                  {hasDiscount && (
                    <Typography variant="caption" color="text.disabled" sx={{ textDecoration: 'line-through', display: 'block' }}>
                      {fmtCurrency(gross)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ pr: 0, width: '15%' }}>
                  <Typography variant="body2" fontWeight={600} color={hasDiscount ? 'error.main' : 'text.primary'}>
                    {fmtCurrency(item.net ?? gross)}
                  </Typography>
                  {hasDiscount && (
                    <Chip
                      label={item.discountType === 'percent' ? `-${item.discountValue}%` : `-${fmtCurrency(item.discountValue)}`}
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{ fontSize: '0.6rem', height: 18, mt: 0.25 }}
                    />
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

// ─── Stripe Payment Form (local copy — mirrors EstimateSharePage version) ─────
function StripePaymentForm({ endpoint, onSuccess, submitLabel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);
    try {
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });
      if (confirmError) throw new Error(confirmError.message);
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentIntentId: paymentIntent.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Payment confirmation failed');
      onSuccess?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      <Box sx={{ mt: 2 }}>
        <Button type="submit" variant="contained" disabled={submitting || !stripe}>
          {submitting ? 'Processing…' : submitLabel}
        </Button>
      </Box>
    </form>
  );
}
